import React from 'react';
import logger from '../utils/logger';
import { View, Text, Modal, TouchableOpacity, StyleSheet, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../contexts/ThemeContext';

export interface AlertButton {
  text: string;
  onPress?: () => void;
  style?: 'default' | 'cancel' | 'destructive';
}

interface CustomAlertProps {
  visible: boolean;
  title: string;
  message: string;
  buttons?: AlertButton[];
  icon?: keyof typeof Ionicons.glyphMap;
  iconColor?: string;
}

export const CustomAlert: React.FC<CustomAlertProps> = ({
  visible,
  title,
  message,
  buttons = [{ text: 'OK' }],
  icon,
  iconColor,
}) => {
  const { theme, isDark } = useTheme();

  const handleButtonPress = (button: AlertButton) => {
    if (button.onPress) {
      button.onPress();
    }
  };

  const getButtonStyle = (style: AlertButton['style']) => {
    switch (style) {
      case 'destructive':
        return { color: theme.colors.error };
      case 'cancel':
        return { color: theme.colors.textSecondary };
      default:
        return { color: theme.colors.primary };
    }
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      statusBarTranslucent
      onRequestClose={() => {
        const cancelButton = buttons.find((b) => b.style === 'cancel');
        if (cancelButton?.onPress) {
          cancelButton.onPress();
        }
      }}
    >
      <View style={styles.overlay}>
        <View style={[styles.alertContainer, { backgroundColor: theme.colors.card }]}>
          {/* Icon (if provided) */}
          {icon && (
            <View style={styles.iconContainer}>
              <Ionicons name={icon} size={48} color={iconColor || theme.colors.primary} />
            </View>
          )}

          {/* Title */}
          <Text style={[styles.title, { color: theme.colors.text }]}>{title}</Text>

          {/* Message */}
          <Text style={[styles.message, { color: theme.colors.textSecondary }]}>{message}</Text>

          {/* Buttons */}
          <View style={styles.buttonsContainer}>
            {buttons.map((button, index) => (
              <TouchableOpacity
                key={index}
                style={[
                  styles.button,
                  index > 0 && styles.buttonMargin,
                  { borderColor: theme.colors.border },
                ]}
                onPress={() => handleButtonPress(button)}
                activeOpacity={0.7}
              >
                <Text
                  style={[
                    styles.buttonText,
                    getButtonStyle(button.style),
                    button.style === 'cancel' && styles.cancelButtonText,
                  ]}
                >
                  {button.text}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  alertContainer: {
    width: '100%',
    maxWidth: 340,
    borderRadius: 16,
    padding: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
  iconContainer: {
    alignItems: 'center',
    marginBottom: 16,
  },
  title: {
    fontSize: 18,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 12,
  },
  message: {
    fontSize: 15,
    textAlign: 'center',
    marginBottom: 24,
    lineHeight: 22,
  },
  buttonsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-evenly',
  },
  button: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  buttonMargin: {
    marginLeft: 12,
  },
  buttonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  cancelButtonText: {
    fontWeight: '500',
  },
});

// Hook to use CustomAlert
export const useCustomAlert = () => {
  const [alertConfig, setAlertConfig] = React.useState<{
    visible: boolean;
    title: string;
    message: string;
    buttons?: AlertButton[];
    icon?: keyof typeof Ionicons.glyphMap;
    iconColor?: string;
  }>({
    visible: false,
    title: '',
    message: '',
  });

  // ✅ CRITICAL: Guard against double-present (SIGABRT on iOS)
  const isPresentingRef = React.useRef(false);
  const pendingAlertRef = React.useRef<any>(null);

  const showAlert = (
    title: string,
    message: string,
    buttons?: AlertButton[],
    icon?: keyof typeof Ionicons.glyphMap,
    iconColor?: string,
  ) => {
    // ✅ Store the pending alert config
    pendingAlertRef.current = {
      title,
      message,
      buttons: buttons || [{ text: 'OK', onPress: () => hideAlert() }],
      icon,
      iconColor,
    };

    // ✅ If already presenting, don't show another (prevents double-present crash)
    if (isPresentingRef.current) {
      logger.log('⚠️ [CustomAlert] Alert already presenting, queuing new one');
      return;
    }

    // ✅ Use requestAnimationFrame to avoid presenting during mount/transition
    // This ensures we present on the next screen refresh cycle
    requestAnimationFrame(() => {
      if (!isPresentingRef.current && pendingAlertRef.current) {
        isPresentingRef.current = true;
        setAlertConfig({
          visible: true,
          ...pendingAlertRef.current,
        });
        logger.log('✅ [CustomAlert] Showing alert:', pendingAlertRef.current.title);
      }
    });
  };

  const hideAlert = () => {
    setAlertConfig((prev) => ({ ...prev, visible: false }));

    // ✅ Reset flag after animation completes (300ms = fade animation)
    // This allows another alert to show if queued
    setTimeout(() => {
      isPresentingRef.current = false;

      // ✅ If there's a pending alert, show it now
      if (pendingAlertRef.current) {
        const pending = pendingAlertRef.current;
        pendingAlertRef.current = null;
        logger.log('📋 [CustomAlert] Showing queued alert:', pending.title);
        showAlert(pending.title, pending.message, pending.buttons, pending.icon, pending.iconColor);
      }
    }, 300);
  };

  const AlertComponent = () => (
    <CustomAlert
      visible={alertConfig.visible}
      title={alertConfig.title}
      message={alertConfig.message}
      buttons={alertConfig.buttons?.map((btn) => ({
        ...btn,
        onPress: () => {
          hideAlert();
          if (btn.onPress) btn.onPress();
        },
      }))}
      icon={alertConfig.icon}
      iconColor={alertConfig.iconColor}
    />
  );

  return { showAlert, hideAlert, AlertComponent };
};
